import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Trash2, AlertTriangle, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const TENANT = "company-a";

const CONTACT_TYPE_LABEL = { message: "Confidential Message", video_request: "Video Request" };

export default function AdminDemoSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/demo-support-requests", { params: { tenant: TENANT } });
      setItems(r.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!selected?.id) { setDetail(null); return; }
    api.get(`/admin/demo-support-requests/${selected.id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [selected?.id]);

  const resetAll = async () => {
    if (!confirm("Delete ALL Company A demo submissions? This cannot be undone.")) return;
    try {
      const r = await api.post("/admin/demo-support-requests/reset", { tenant: TENANT });
      toast.success(`Reset complete — ${r.data.deleted} record(s) deleted`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div data-testid="admin-demo-submissions">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-heading text-2xl tracking-tight text-slate-900">Demo Submissions</h2>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-800">
              <Sparkles className="w-3 h-3" strokeWidth={2.4} /> Company A — Demo
            </span>
          </div>
          <p className="text-sm text-slate-500">Isolated <code className="text-xs bg-slate-100 px-1 rounded">demo_support_requests</code> collection. Auto-expires after 30 days.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="demo-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:border-[#4A6FA5]">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2.2} /> Refresh
          </button>
          <button onClick={resetAll} data-testid="demo-reset" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-300 text-sm text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4" strokeWidth={2.2} /> Reset Company A Demo Data
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500" data-testid="demo-empty">No Company A demo submissions yet. Start one at <code className="text-xs bg-slate-100 px-1 rounded">/care-portal/demo/company-a/employee</code>.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.16em] text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Name</th>
                  <th className="text-left px-5 py-3 font-medium">Type</th>
                  <th className="text-left px-5 py-3 font-medium">Email</th>
                  <th className="text-left px-5 py-3 font-medium">Status</th>
                  <th className="text-left px-5 py-3 font-medium">Submitted</th>
                  <th className="text-left px-5 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`demo-row-${r.id}`}>
                    <td className="px-5 py-4 text-slate-900 font-medium">{r.first_name} {r.last_name}</td>
                    <td className="px-5 py-4"><span className="inline-flex px-2 py-0.5 rounded-full bg-[#F0F4F8] text-[#3B597B] text-[11px] font-semibold uppercase tracking-wider">{CONTACT_TYPE_LABEL[r.contact_type] || r.contact_type}</span></td>
                    <td className="px-5 py-4 text-slate-600">{r.email}</td>
                    <td className="px-5 py-4 text-slate-600">{r.status}</td>
                    <td className="px-5 py-4 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-5 py-4"><button onClick={() => setSelected(r)} data-testid={`demo-view-${r.id}`} className="text-[#4A6FA5] hover:text-[#3B597B] text-sm font-medium">View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="demo-detail-drawer">
          <div className="flex-1 bg-slate-900/30" onClick={() => setSelected(null)} />
          <div className="w-full max-w-xl bg-white border-l border-slate-200 overflow-y-auto p-6 md:p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2"><span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">Demo Submission</span><span className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-semibold text-amber-800">Company A — DEMO</span></div>
                <h3 className="font-heading text-2xl text-slate-900">{selected.first_name} {selected.last_name}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-900" data-testid="demo-close">✕</button>
            </div>
            <dl className="space-y-3 text-sm">
              <Row label="Contact Type" value={CONTACT_TYPE_LABEL[selected.contact_type] || selected.contact_type} />
              <Row label="Email" value={selected.email} />
              <Row label="Phone" value={selected.phone || "—"} />
              <Row label="Preferred" value={selected.preferred_contact} />
              <Row label="Best Time" value={selected.best_time_to_contact || "—"} />
              <Row label="Status" value={selected.status} />
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-1.5">Reason / Message</dt>
                <dd className="text-slate-700 leading-relaxed bg-[#F3F5F7] rounded-lg p-3.5">{selected.reason}</dd>
              </div>
              <Row label="Submitted" value={new Date(selected.created_at).toLocaleString()} />
            </dl>
            {detail && detail.activities && detail.activities.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h4 className="font-heading text-sm text-slate-900 mb-3">Chaplain Activity Log</h4>
                <ul className="space-y-2">
                  {detail.activities.map((a) => (
                    <li key={a.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="inline-flex px-2 py-0.5 rounded-full bg-[#F0F4F8] text-[#3B597B] font-semibold uppercase tracking-wider">{a.contact_type}</span>
                        <span className="text-slate-500">{a.date} {a.time}</span>
                      </div>
                      <p className="mt-1 text-slate-700">{a.staff_name}</p>
                      {a.note && <p className="mt-1 text-slate-600">{a.note}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-6 flex items-start gap-2 p-3 rounded-lg bg-[#FFF8ED] border border-[#F5D68A]/60">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-[#B8791A]" strokeWidth={2} />
              <p className="text-xs text-[#7A4F0F]">Demo record only — never mixed with production support_requests.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4"><dt className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">{label}</dt><dd className="text-slate-800 text-right">{value}</dd></div>
  );
}
